import { useContext, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

import BreadCrumb from "../components/breadCrumb/BreadCrumb.jsx";
import DefineReportingPeriods from "../components/school/DefineReportingPeriods.jsx";
import getCombinedGrades from "../utils/SchoolAdmin/getCombinedGrades.jsx";
import LoadingSvg from "../components/loadingSvg/LoadingSvg.jsx";
import LocationDao from "../dao/LocationDao.js";
import SchoolHome from "../components/school/SchoolHome.jsx";
import SchoolLevelExpectations from "../components/school/SchoolLevelExpectations.jsx";
import SchoolTemplate from "../components/school/SchoolTemplate.jsx";
import TeacherOverviewHome from "../components/teacherOverview/TeacherOverviewHome.jsx";
import SidebarMenu from "../components/sidebar/SidebarMenu.jsx";
import { useEnsureSchoolTemplatesForLocation } from "../components/contextProvider/ContextSetter.jsx";

import { EVALUATION_SITE } from "../utils/auth/config.js";
import { GlobalContext } from "../components/contextProvider/ContextProvider";

import "../styles/global/Global.scss";
import "../styles/Home.scss";
import StudentSearch from "./StudentSearch.jsx";

/**
 * Promote Region Overview drill-down handoff into viewedSchoolKey before SchoolAdmin mounts.
 * /home/overview mounts this segment before Home's effect can finish the same promotion.
 * @returns {string}
 */
export const resolveViewedSchoolKeyFromStorage = () => {
    const initKey = sessionStorage.getItem("initViewedSchoolKey");
    if (initKey) {
        sessionStorage.setItem("viewedSchoolKey", initKey);
        sessionStorage.removeItem("initViewedSchoolKey");
        return initKey;
    }
    return sessionStorage.getItem("viewedSchoolKey") || "";
};

/**
 * This is the SchoolAdminHome page for epr
 * @name SchoolAdmin
 * @returns {JSX.Element}
 */
/**
 * Compute the display status for a school's EPR template.
 *
 * @param {object|null} schoolLevelTemplateDto
 * @param {boolean}     isDistrictTemplateReleased
 * @param {object|null} districtTemplateDto
 * @param {boolean}     isTemplateLoading
 * @param {Array|null}  applicableGrades  - grade tag objects from the location API
 * @returns {{ displayStatus: string, status: string }}
 */
export const computeTemplateStatus = (
    schoolLevelTemplateDto,
    isDistrictTemplateReleased,
    districtTemplateDto,
    isTemplateLoading,
    applicableGrades
) => {
    const hasValidSemesters =
        schoolLevelTemplateDto && (schoolLevelTemplateDto.firstSemester || schoolLevelTemplateDto.secondSemester);

    // Build a Set of applicable grade levels for fast lookup.
    // applicableGrades uses "KINDERGARTEN" for K but gradeToSubjects uses "K",
    // so mirror the same special-case handling used elsewhere in the app.
    const isKinderApplicable = (applicableGrades ?? []).some((g) => g.tag === "KINDERGARTEN");
    const applicableGradeLevels = new Set([
        ...(applicableGrades ?? []).map((g) => g.tag),
        ...(isKinderApplicable ? ["K"] : [])
    ]);

    const allTemplatesReleased =
        hasValidSemesters &&
        Object.values(schoolLevelTemplateDto)
            .filter(Boolean)
            .flatMap((semester) => semester?.gradeToSubjects ?? [])
            .filter((subject) => applicableGradeLevels.size === 0 || applicableGradeLevels.has(subject.gradeLevel))
            .every((subject) => subject.templatesReleased === true);

    // Status cascades — last matching condition wins.
    let result = { displayStatus: "", status: "" };

    if (!districtTemplateDto || (districtTemplateDto && isDistrictTemplateReleased === false)) {
        result = { displayStatus: "Waiting on District", status: "waiting" };
    }
    if (districtTemplateDto && isDistrictTemplateReleased && !hasValidSemesters && !isTemplateLoading) {
        result = { displayStatus: "Ready to Pull", status: "ready" };
    }
    if (hasValidSemesters) {
        result = { displayStatus: "In Progress", status: "in-progress" };
    }
    if (hasValidSemesters && allTemplatesReleased) {
        result = { displayStatus: "Released to Teachers", status: "released" };
    }

    return result;
};

const SchoolAdmin = ({ isDistrict, isPK12 }) => {
    const [viewedSchoolKey, setViewedSchoolKey] = useState(() => resolveViewedSchoolKeyFromStorage());
    const [status, setStatus] = useState({ displayStatus: "", status: "" });
    const [combinedGrades, setCombinedGrades] = useState(null);
    const [selectedGrade, setSelectedGrade] = useState("K");
    const [selectedContentArea, setSelectedContentArea] = useState("");

    const [isDistrictTemplateReleased, setIsDistrictTemplateReleased] = useState(false);
    const [templateTouchedFlag, setTemplateTouchedFlag] = useState(true);
    const [loader, setLoader] = useState(false);
    const [selectedLink, setSelectedLink] = useState("HOME");
    const [navCollapsed, setNavCollapsed] = useState(false);
    const [labelObj, setLabelObj] = useState([{ label: "Home", path: "/home" }]);
    const [applicableGrades, setApplicableGrades] = useState(null);
    const [locationKey, setLocationKey] = useState(() => {
        const storedKey = resolveViewedSchoolKeyFromStorage();
        if ((isDistrict || isPK12) && storedKey) return storedKey;
        return null;
    });
    const [viewSchoolAdmin, setViewSchoolAdmin] = useState(false);

    const { dispatch, state } = useContext(GlobalContext);
    const {
        allLocationDtos,
        legacyLocationDtos,
        districtTemplateDto,
        isDistrictViewingSchool,
        schoolTemplatesByLocation,
        schoolTemplatesLoadingByLocation,
        timePeriodDto,
        token,
        userDetails
    } = state;

    const schoolLevelTemplateDto = schoolTemplatesByLocation?.[locationKey];
    const isSchoolTemplate = Boolean(schoolLevelTemplateDto?.firstSemester || schoolLevelTemplateDto?.secondSemester);

    // districtTemplateDto is undefined while the fetch is in flight, null once confirmed absent.
    // If there's no district template, a school template can't exist either — no point waiting.
    const districtTemplateFetchComplete = districtTemplateDto !== undefined;
    const hasAttemptedTemplateLoad =
        locationKey != null && schoolTemplatesLoadingByLocation?.[locationKey] !== undefined;
    const isTemplateLoading =
        !districtTemplateFetchComplete ||
        (districtTemplateDto &&
            locationKey != null &&
            (!hasAttemptedTemplateLoad || schoolTemplatesLoadingByLocation[locationKey]));
    const isLoading = loader || isTemplateLoading;

    // Standardized fetcher for school templates
    const ensureSchoolTemplates = useEnsureSchoolTemplatesForLocation();

    const predominantSchool = userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL;

    const locationName = useMemo(() => {
        if (!locationKey || (!allLocationDtos && !legacyLocationDtos)) return "";
        const matchKey = (l) => String(l.key) === String(locationKey);
        const loc = allLocationDtos?.find(matchKey) || legacyLocationDtos?.find(matchKey);
        return loc ? loc.name : "";
    }, [locationKey, allLocationDtos, legacyLocationDtos]);

    const location = useLocation();
    const { pathname } = location;

    const handleClearSchoolOnDelete = () => {
        setCombinedGrades(null);
        dispatch({
            type: "SchoolLevelTemplateDto",
            schoolLevelTemplateDto: null
        });
        if (locationKey) {
            dispatch({
                type: "SchoolTemplatesByLocationMerge",
                schoolTemplatesByLocation: { [locationKey]: null }
            });
        }
    };

    const handleSchoolChange = (e) => {
        dispatch({
            type: "SchoolLevelTemplateDto",
            schoolLevelTemplateDto: null
        });
        setTemplateTouchedFlag(true);
        setCombinedGrades(null);

        const { value } = e.target;
        setViewedSchoolKey(value);
        setLocationKey(value);

        if (value) {
            sessionStorage.setItem("viewedSchoolKey", value);
            dispatch({
                type: "IsDistrictViewingSchool",
                isDistrictViewingSchool: true
            });
        } else {
            sessionStorage.removeItem("viewedSchoolKey");
        }
    };
    /**
     * Sets the selected link based of the path to make the nav work on refresh
     */
    useEffect(() => {
        if (isDistrictViewingSchool || viewedSchoolKey) {
            setLocationKey(viewedSchoolKey || null);
        } else if (userDetails) {
            setLocationKey(userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationKey);
        }
    }, [isDistrictViewingSchool, userDetails, viewedSchoolKey]);

    /**
     * Sets the selected link based of the path to make the nav work on refresh
     */
    useEffect(() => {
        let trimmedPath;
        if (pathname === "/") {
            trimmedPath = "HOME";
        } else {
            trimmedPath = pathname.slice(1).toUpperCase();
        }
        setSelectedLink(trimmedPath);
    }, [pathname]);

    /**
     * Get the correct status of school epr template
     */
    useEffect(() => {
        setStatus(
            computeTemplateStatus(
                schoolLevelTemplateDto,
                isDistrictTemplateReleased,
                districtTemplateDto,
                isTemplateLoading,
                applicableGrades
            )
        );
    }, [isDistrictTemplateReleased, schoolLevelTemplateDto, districtTemplateDto, isTemplateLoading, applicableGrades]);

    /**
     * Get location information to know which grades are applicable
     */
    useEffect(() => {
        let isCurrent = true;
        setApplicableGrades(null);
        if (locationKey && token) {
            const options = {
                action: "locationByKeyRead",
                key: locationKey,
                token
            };
            setLoader(true);
            LocationDao(options)
                .then((response) => {
                    if (isCurrent && response?.data?.payload) {
                        const gradeTags = response.data.payload.tags.filter((tag) => tag.type === "GRADE");
                        setApplicableGrades(gradeTags);
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    if (isCurrent) {
                        setLoader(false);
                    }
                });
        }
        return () => {
            isCurrent = false;
        };
    }, [locationKey, token]);

    /**
     * Get school template if it exists and has not been fetched yet.
     * A school template can only exist once the district template has been created,
     * so we gate this fetch on districtTemplateDto being truthy.
     */
    useEffect(() => {
        if (districtTemplateDto && locationKey && locationKey !== "" && templateTouchedFlag) {
            ensureSchoolTemplates({ locationKey, forceRefetch: true });
            setTemplateTouchedFlag(false);
        }
    }, [districtTemplateDto, locationKey, templateTouchedFlag, ensureSchoolTemplates]);

    /**
     * Check for released (if 'some' (any) grade is released they are all released and template is released)
     */
    useEffect(() => {
        if (districtTemplateDto?.profileId) {
            const isReleased = districtTemplateDto.gradeToSubjects.some((grade) => grade.templatesReleased === true);
            setIsDistrictTemplateReleased(isReleased);
        }
    }, [districtTemplateDto]);

    /**
     * Take first and second semester dtos and combine into an array of grade objects
     */
    useEffect(() => {
        const schoolLevelTemplateDto = schoolTemplatesByLocation?.[locationKey];
        if (schoolLevelTemplateDto) {
            const combined = getCombinedGrades(
                schoolLevelTemplateDto.firstSemester,
                schoolLevelTemplateDto.secondSemester
            );
            setCombinedGrades(combined);
        } else {
            setCombinedGrades(null);
        }
    }, [schoolTemplatesByLocation, locationKey]);

    /**
     * Take first and second semester dtos and combine into an array of grade objects
     */
    useEffect(() => {
        const hasValidSchoolContext = !isDistrict || ((isDistrict || isPK12) && viewedSchoolKey !== "");
        // District Admins might not have a predominantSchool but they can have a viewedSchoolKey
        if (hasValidSchoolContext && (predominantSchool || ((isDistrict || isPK12) && viewedSchoolKey))) {
            setViewSchoolAdmin(true);
        } else {
            setViewSchoolAdmin(false);
        }
    }, [isDistrict, predominantSchool, viewedSchoolKey, isPK12]);

    return (
        <div className="gutter-95" style={{ padding: "0px 0px" }}>
            {!predominantSchool && !isDistrict && (
                <div
                    style={{
                        border: "1px solid black",
                        borderRadius: "5px",
                        padding: "12px 24px",
                        margin: "24px 0px",
                        width: "fit-content"
                    }}
                >
                    <h5>No Location Found</h5>
                    <div>A primary location is required before you can access Report Card.</div>
                    <div>
                        Please update your employee profile{" "}
                        <a href={`${EVALUATION_SITE}/profile`} target="_blank" rel="noreferrer">
                            here.
                        </a>
                    </div>
                </div>
            )}
            {(isDistrictViewingSchool || isDistrict) && !viewedSchoolKey && (
                <div className="select-school-container">
                    <label id="label" htmlFor="state" className="select-school-label">
                        Select a school from the list: *
                    </label>
                    <select
                        aria-label="selected school ID"
                        className="participating-schools-dropdown"
                        id="selectedSchoolId"
                        name="selectedSchoolId"
                        onChange={(e) => handleSchoolChange(e)}
                        required
                        value={viewedSchoolKey}
                    >
                        {allLocationDtos && (
                            <>
                                <option value="">Select a School</option>
                                {allLocationDtos.map((obj, index) => {
                                    const optionKey = `opt-${index}`;
                                    const { name, key } = obj;
                                    return (
                                        <option key={optionKey} value={key}>
                                            {name}
                                        </option>
                                    );
                                })}
                            </>
                        )}
                    </select>
                </div>
            )}
            {viewSchoolAdmin && (
                <>
                    {isDistrictViewingSchool && locationName && (
                        <div className="golden-rod golden-rod--viewport-wide">
                            <strong>Viewing School</strong> - {locationName}
                        </div>
                    )}
                    <BreadCrumb style={{ marginTop: "-10px" }} labelObj={labelObj} setSelectedLink={setSelectedLink} />
                    <div style={{ display: "flex" }}>
                        <SidebarMenu
                            isSchoolTemplate={isSchoolTemplate}
                            navCollapsed={navCollapsed}
                            selectedLink={selectedLink}
                            setLabelObj={setLabelObj}
                            setNavCollapsed={setNavCollapsed}
                            setSelectedLink={setSelectedLink}
                            isPK12={isPK12}
                        />

                        {selectedLink === "HOME" && (
                            <SchoolHome
                                applicableGrades={applicableGrades}
                                combinedGrades={combinedGrades}
                                loader={isLoading}
                                status={status.displayStatus}
                                statusClass={status.status}
                                timePeriodDto={timePeriodDto}
                            />
                        )}
                        {selectedLink === "HOME/STUDENT-SEARCH" && <StudentSearch />}
                        {selectedLink === "HOME/SLES" && (
                            <SchoolLevelExpectations
                                fullYearTimePeriodKey={timePeriodDto?.find((period) => period.name === "Full Year").key}
                                locationKey={locationKey}
                            />
                        )}
                        {!isLoading && (
                            <>
                                {selectedLink === "HOME/OVERVIEW" && <TeacherOverviewHome locationKey={locationKey} />}
                                {selectedLink === "HOME/TEMPLATE" && (
                                    <SchoolTemplate
                                        applicableGrades={applicableGrades}
                                        combinedGrades={combinedGrades}
                                        handleClearSchoolOnDelete={handleClearSchoolOnDelete}
                                        loader={isLoading}
                                        setLabelObj={setLabelObj}
                                        setSelectedLink={setSelectedLink}
                                        setTemplateTouchedFlag={setTemplateTouchedFlag}
                                        status={status.status}
                                        isSchoolTemplate={isSchoolTemplate}
                                        locationKey={locationKey}
                                        locationName={locationName}
                                    />
                                )}
                                {selectedLink.includes("HOME/TEMPLATE/DEFINE") && (
                                    <DefineReportingPeriods
                                        applicableGrades={applicableGrades}
                                        combinedGrades={combinedGrades}
                                        isDistrictTemplateReleased={isDistrictTemplateReleased}
                                        isSchoolTemplate={isSchoolTemplate}
                                        loader={isLoading}
                                        selectedContentArea={selectedContentArea}
                                        selectedGrade={selectedGrade}
                                        setLabelObj={setLabelObj}
                                        setSelectedContentArea={setSelectedContentArea}
                                        setSelectedGrade={setSelectedGrade}
                                        setSelectedLink={setSelectedLink}
                                        setTemplateTouchedFlag={setTemplateTouchedFlag}
                                    />
                                )}
                            </>
                        )}
                        {isLoading && <LoadingSvg />}
                    </div>
                </>
            )}
        </div>
    );
};

export default SchoolAdmin;

SchoolAdmin.propTypes = {
    isDistrict: PropTypes.bool,
    isPK12: PropTypes.bool
};

SchoolAdmin.defaultProps = {
    isDistrict: false,
    isPK12: false
};
